

































































































































































































































































import { AccessLevelDynamicBadgeCompact, CopyToClipboardButton } from '@/app/components';
import { useFeatureFlags, useFilters, useOdeonCustomisation } from '@/app/composable';
import store from '@/app/store';
import { LockClosedIcon } from '@vue-hero-icons/solid';
import { PropType, computed, defineComponent, ref } from '@vue/composition-api';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import * as R from 'ramda';
import { TaskExecutionStatus, WorkflowExecutionStatus } from '../constants';
import { IndexedApolloPipeline, IndexedApolloPipelineTask, IndexedHarvesterSourceType } from '../types';
import { Icon } from '@iconify/vue2';
import { match } from 'ts-pattern';

dayjs.extend(utc);

export default defineComponent({
    name: 'DataCheckinPipelineOverview',
    components: {
        LockClosedIcon,
        AccessLevelDynamicBadgeCompact,
        Icon,
        CopyToClipboardButton,
    },
    props: {
        pipeline: {
            type: Object as PropType<IndexedApolloPipeline>,
            required: true,
        },
        alert: {
            type: Object as PropType<{ entityId: string | number; alertId: string; enabled: boolean }>,
            required: false,
        },
    },
    setup(props, { emit, root }: { emit: any; root: any }) {
        const { formatDate, formatDateTime } = useFilters();
        const showTasks = ref<boolean>(false);
        const { flag } = useFeatureFlags();
        const isAlertsEnabled = flag('alerts');
        const { taskTypeLabel } = useOdeonCustomisation();
        const canRetry = computed(() => props.pipeline.executionStatus === WorkflowExecutionStatus.Cancelled);

        const orderedTasks = computed(() => R.sort(R.ascend<any>(R.prop('order')), props.pipeline.tasks));

        const statusTask: any = computed(() => {
            for (let idx = 0; idx < orderedTasks.value.length; idx += 1)
                if ([TaskExecutionStatus.Draft, TaskExecutionStatus.Updating].includes(orderedTasks.value[idx].status))
                    return orderedTasks.value[idx];

            for (let idx = 0; idx < orderedTasks.value.length; idx += 1)
                if (orderedTasks.value[idx].status !== TaskExecutionStatus.Completed) {
                    return orderedTasks.value[idx];
                }

            return orderedTasks.value[orderedTasks.value.length - 1];
        });

        const statusThemeCalculator = (status: WorkflowExecutionStatus | TaskExecutionStatus) =>
            match<
                WorkflowExecutionStatus | TaskExecutionStatus,
                { icon: string; tooltip: string; textColour: string; bgColour: string }
            >(status)
                .with(WorkflowExecutionStatus.Draft, TaskExecutionStatus.Draft, () => {
                    return {
                        icon: 'solar:menu-dots-circle-bold',
                        textColour: 'text-blue-700',
                        bgColour: 'bg-blue-700',
                        tooltip: `Under configuration: ${statusTask.value.type}`,
                    };
                })
                .with(WorkflowExecutionStatus.Updating, TaskExecutionStatus.Updating, () => {
                    return {
                        icon: 'solar:server-square-update-bold',
                        textColour: 'text-blue-700',
                        bgColour: 'bg-blue-700',
                        tooltip: `Configuration under update: ${statusTask.value.type}`,
                    };
                })
                .with(WorkflowExecutionStatus.Completed, TaskExecutionStatus.Completed, () => {
                    return {
                        icon: 'solar:check-circle-bold',
                        textColour: 'text-green-700',
                        bgColour: 'bg-green-700',
                        tooltip: 'Last execution completed successfully',
                    };
                })
                .with(WorkflowExecutionStatus.Failed, TaskExecutionStatus.Failed, () => {
                    return {
                        icon: 'solar:confounded-circle-bold',
                        textColour: 'text-danger-700',
                        bgColour: 'bg-danger-700',
                        tooltip: 'Last execution failed',
                    };
                })
                .with(WorkflowExecutionStatus.Suspended, () => {
                    return {
                        icon: 'solar:close-circle-bold',
                        textColour: 'text-warn-700',
                        bgColour: 'bg-warn-700',
                        tooltip: 'Last execution was suspended',
                    };
                })
                .with(WorkflowExecutionStatus.Skipped, () => {
                    return {
                        icon: 'solar:danger-circle-bold',
                        textColour: 'text-neutral-700',
                        bgColour: 'bg-neutral-700',
                        tooltip: 'Last execution was skipped',
                    };
                })
                .with(WorkflowExecutionStatus.Cancelled, TaskExecutionStatus.Cancelled, () => {
                    return {
                        icon: 'solar:minus-circle-bold',
                        textColour: 'text-neutral-700',
                        bgColour: 'bg-neutral-700',
                        tooltip: 'Last execution was cancelled',
                    };
                })
                .with(WorkflowExecutionStatus.Ready, TaskExecutionStatus.Ready, () => {
                    return {
                        icon: 'solar:soundwave-circle-bold',
                        textColour: 'text-purple-700',
                        bgColour: 'bg-purple-700',
                        tooltip: 'Ready and execution',
                    };
                })
                .with(WorkflowExecutionStatus.Queued, TaskExecutionStatus.Queued, () => {
                    return {
                        icon: 'solar:clock-circle-bold',
                        textColour: 'text-purple-700',
                        bgColour: 'bg-purple-700',
                        tooltip: 'Queued for execution',
                    };
                })
                .with(WorkflowExecutionStatus.Deprecated, TaskExecutionStatus.Deprecated, () => {
                    return {
                        icon: 'solar:forbidden-circle-bold',
                        textColour: 'text-neural-700',
                        bgColour: 'bg-neural-700',
                        tooltip: 'Deprecated',
                    };
                })
                .with(WorkflowExecutionStatus.Running, TaskExecutionStatus.Running, () => {
                    return {
                        icon: 'solar:bolt-circle-bold',
                        textColour: 'text-sky-700 pulse',
                        bgColour: 'bg-sky-700 pulse',
                        tooltip: 'Pipeline is currently running',
                    };
                })
                .exhaustive();

        const status = computed(() => statusThemeCalculator(props.pipeline.executionStatus));

        const harvesterSource = computed((): { label: string; colour: string; fileType?: string } | null => {
            let harvesterOption: { label: string; colour: string; fileType?: string } | null = null;
            if (props.pipeline?.harvesterSource) {
                switch (props.pipeline.harvesterSource) {
                    case IndexedHarvesterSourceType.Api:
                        harvesterOption = { label: 'Data Provider API', colour: 'bg-orange-300' };
                        break;
                    case IndexedHarvesterSourceType.InternalApi:
                        harvesterOption = { label: 'Platform API', colour: 'bg-green-300' };
                        break;
                    case IndexedHarvesterSourceType.File:
                        harvesterOption = {
                            label: 'File',
                            colour: 'bg-blue-300',
                        };
                        break;
                    case IndexedHarvesterSourceType.LargeFiles:
                        harvesterOption = {
                            label: 'Large Files',
                            colour: 'bg-slate-200',
                        };
                        break;
                    case IndexedHarvesterSourceType.Kafka:
                        harvesterOption = {
                            label: 'Kafka',
                            colour: 'bg-cherry-300',
                        };
                        break;
                    case IndexedHarvesterSourceType.ExternalKafka:
                        harvesterOption = {
                            label: 'External Kafka',
                            colour: 'bg-cherry-300',
                        };
                        break;
                    case IndexedHarvesterSourceType.MQTT:
                        harvesterOption = {
                            label: 'MQTT',
                            colour: 'bg-olive-300',
                        };
                        break;
                    case IndexedHarvesterSourceType.ExternalMQTT:
                        harvesterOption = {
                            label: 'External MQTT',
                            colour: 'bg-olive-300',
                        };
                        break;
                    case IndexedHarvesterSourceType.BigQuery:
                        harvesterOption = {
                            label: 'BigQuery',
                            colour: 'bg-purple-200',
                        };
                        break;
                    case IndexedHarvesterSourceType.SQL:
                        harvesterOption = {
                            label: 'SQL',
                            colour: 'bg-teal-300',
                        };
                        break;
                    case IndexedHarvesterSourceType.SAPHana:
                        harvesterOption = {
                            label: 'SAP Hana',
                            colour: 'bg-slateblue-300',
                        };
                        break;
                    default:
                        return { label: 'No harvester option', colour: 'bg-red-300' };
                }

                if (
                    props.pipeline.harvesterSource !== IndexedHarvesterSourceType.InternalApi &&
                    props.pipeline.fileType
                ) {
                    harvesterOption['fileType'] = ['parquet', 'other'].includes(props.pipeline.fileType)
                        ? props.pipeline.fileType
                        : props.pipeline.fileType.toUpperCase();
                }
            }
            return harvesterOption;
        });

        const user = computed(() => store.state.auth.user);
        const isUserPipelineCreator = computed(() => user.value.id === Number(props.pipeline?.createdBy?.id));

        const editPipeline = async (pipeline: any) => {
            if (props.pipeline.executionStatus === WorkflowExecutionStatus.Suspended) {
                (root as any).$toastr.w(
                    `This data check-in pipeline is suspended as its output asset (dataset) was deleted. You need to edit the Loader step and configure a new output asset in order to be able to use this pipeline again.`,
                    'Warning',
                );
            } else {
                emit('edit', pipeline);
            }
        };

        const appendData = async (pipeline: any) => {
            root.$router.push({ name: 'harvester', params: { id: pipeline.id } });
        };

        const displayAnyWarnings = (task: IndexedApolloPipelineTask): void => {
            if (task.type === 'harvester' && props.pipeline.executionStatus === WorkflowExecutionStatus.Suspended)
                (root as any).$toastr.w(
                    `This data check-in pipeline is suspended as its output asset (dataset) was deleted. You need to edit the Loader step and configure a new output asset in order to be able to use this pipeline again`,
                    'Warning',
                );
        };

        const toTasks = (name: string, pipeline: any) => {
            return { name, params: { id: pipeline.id } };
        };

        const destroyPipeline = async (pipeline: any) => {
            if (!pipeline.canUse) {
                (root as any).$toastr.e('The Data Check-in Pipeline is locked', 'Error');
            } else {
                emit('destroy', pipeline);
            }
        };

        const goToAsset = (assetId: number) => {
            root.$router.push({ name: 'assets:view', params: { id: assetId } });
        };

        const isFinalized = computed(() => {
            if (props.pipeline.executionStatus === WorkflowExecutionStatus.Suspended) return true;
            return props.pipeline.tasks.every(
                (task: any) =>
                    !!task && ![WorkflowExecutionStatus.Draft, WorkflowExecutionStatus.Updating].includes(task.status),
            );
        });

        const accessLevel = computed(() => props.pipeline && props.pipeline?.accessLevel);

        const editAlert = () => {
            if (props.alert)
                root.$router.push({
                    name: 'alerts:edit',
                    params: { id: props.alert.alertId, backTo: 'data-checkin-jobs' },
                });
        };

        return {
            IndexedHarvesterSourceType,
            dayjs,
            formatDate,
            formatDateTime,
            WorkflowExecutionStatus,
            TaskExecutionStatus,
            orderedTasks,
            statusTask,
            canRetry,
            isUserPipelineCreator,
            editPipeline,
            appendData,
            toTasks,
            destroyPipeline,
            isFinalized,
            accessLevel,
            harvesterSource,
            isAlertsEnabled,
            goToAsset,
            displayAnyWarnings,
            editAlert,
            status,
            statusThemeCalculator,
            showTasks,
            taskTypeLabel,
        };
    },
});
